import {
  cn,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogProps,
  RiCloseLine,
} from 'component-library';
import { FC, forwardRef, ReactNode, useImperativeHandle, useState } from 'react';

import { IconButtonWidget, IconButtonWidgetProps } from '@/components/widgets/a/Button/IconButton';
import { TextContainer } from '@/components/widgets/a/Text/Text';

export const DialogBase: FC<DialogProps> = ({ ...delegated }) => {
  return <Dialog {...delegated} />;
};

export type DialogWidgetHandle = {
  openDialog: () => void;
  closeDialog: () => void;
};

type DialogButtonWidgetProps = {
  button?: IconButtonWidgetProps;
};

export const DialogWidget: FC<DialogButtonWidgetProps> = forwardRef<DialogWidgetHandle, DialogButtonWidgetProps>(
  ({ button, ...delegated }, ref) => {
    const [isOpen, setIsOpen] = useState(false);

    useImperativeHandle(ref, () => ({
      openDialog: () => setIsOpen(true),
      closeDialog: () => setIsOpen(false),
    }));

    const handleOpenDialog = () => {
      setIsOpen((prev) => !prev);
    };

    return (
      <>
        <IconButtonWidget onClick={handleOpenDialog} icon={button?.icon ?? ''} {...button} />
        <DialogBase open={isOpen} onOpenChange={handleOpenDialog} {...delegated} />
      </>
    );
  },
);
DialogWidget.displayName = 'DialogWidget';

type DialogLayoutWidgetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
};

export const DialogLayoutWidget = ({ open, onOpenChange, children, ...delegated }: DialogLayoutWidgetProps) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange} {...delegated}>
      {children}
    </Dialog>
  );
};

type DialogHeaderWidgetProps = {
  children: ReactNode;
  className?: string;
};

export const DialogHeaderWidget = ({ children, className, ...delegated }: DialogHeaderWidgetProps) => {
  return (
    <DialogHeader className={className} {...delegated}>
      {children}
    </DialogHeader>
  );
};

export const DialogCloseWidget = ({
  className,
  text,
  i18nPath,
  showIcon = true,
  buttonClassName,
  ...delegated
}: {
  showIcon?: boolean;
  className?: string;
  text?: string;
  variant?: 'contained' | 'outline' | 'text';
  i18nPath?: string;
  buttonClassName?: string;
}) => {
  return (
    <DialogClose className={cn('aspect-square rounded-full p-2', className)} {...delegated}>
      {showIcon && <RiCloseLine className='h-8 w-8' />}
      {(text || i18nPath) && (
        <TextContainer
          className={cn(
            'rounded-full bg-transparent text-primary-100 hover:bg-primary-12 px-5 py-3 typography-h4 ',
            buttonClassName,
          )}
          i18nPath={i18nPath}
          text={text}
        />
      )}
    </DialogClose>
  );
};

type DialogContentWidgetProps = {
  children: ReactNode;
  className?: string;
  withXButton?: boolean;
};

export const DialogContentWidget = ({
  children,
  className,
  withXButton = true,
  ...delegated
}: DialogContentWidgetProps) => {
  return (
    <DialogContent className={className} withXButton={withXButton} {...delegated}>
      {children}
    </DialogContent>
  );
};

type DialogBodyWidgetProps = {
  children: ReactNode;
  className?: string;
};

export const DialogBodyWidget = ({ className, children, ...delegated }: DialogBodyWidgetProps) => {
  return (
    <DialogBody className={className} {...delegated}>
      {children}
    </DialogBody>
  );
};

export const DialogFooterWidget = ({ className, children, ...delegated }: DialogBodyWidgetProps) => {
  return (
    <DialogFooter className={className} {...delegated}>
      {children}
    </DialogFooter>
  );
};
